import React, { useCallback, useMemo, useState } from 'react';
import { Select, Spin } from 'antd';
import { SelectValue } from 'antd/lib/select';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { getZonesByRegion } from '../service/MySrcmService';

const { Option } = Select;

interface OptionData {
	value: string;
}

interface RemoteSingleSelectProps {
	onChange?: (values: Array<OptionData>) => void;
	value?: Array<string>;
	functionarySrcmId?: string;
	regionId?: number;
}

const RemoteSingleSelect: React.FC<RemoteSingleSelectProps> = ({
	regionId,
	onChange,
	value,
	functionarySrcmId,
}: RemoteSingleSelectProps) => {
	const [options, setOptions] = useState<OptionData[]>([]);
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();

	const values = useMemo(() => {
		return value?.map((s) => ({
			value: s,
		}));
	}, [value]);

	const fetchData = useCallback(
		async (searchValue: string) => {
			setLoading(true);

			if (functionarySrcmId && regionId) {
				const zonesPage = await getZonesByRegion(regionId);
				let filtered = zonesPage.results;

				if (searchValue) {
					filtered = filtered.filter((item) =>
						item.name?.toLowerCase().includes(searchValue.toLowerCase())
					);
				}

				const transformedOptions: OptionData[] = filtered
					.filter((item): item is { name: string } => !!item.name)
					.map((item) => ({
						value: item.name,
					}));

				setOptions(transformedOptions);
			} else {
				setOptions([]);
			}

			setLoading(false);
		},
		[functionarySrcmId, regionId]
	);

	const debouncedFetchData = useMemo(
		() => debounce(fetchData, 500),
		[fetchData]
	);

	const handleSearch = useCallback(
		(searchKey: SelectValue) => {
			void debouncedFetchData(searchKey as string);
		},
		[debouncedFetchData]
	);

	const handleFocus = useCallback(() => {
		if (options.length === 0) {
			void fetchData('');
		}
	}, [fetchData, options.length]);

	return (
		<Select
			showSearch
			mode="multiple"
			defaultActiveFirstOption={false}
			filterOption={false}
			placeholder={t('searchZones')}
			notFoundContent={loading ? <Spin size="small" /> : null}
			onFocus={handleFocus}
			onSearch={handleSearch}
			onChange={onChange}
			labelInValue
			value={values}
			suffixIcon={null}
			className="font-sans text-sm text-secondary-gray"
			disabled={!regionId}
		>
			{options.map((option) => (
				<Option key={option.value} value={option.value}>
					{option.value}
				</Option>
			))}
		</Select>
	);
};

export default RemoteSingleSelect;
